import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Sparkles, Heart, Brain, Apple, Info } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import './BorderTraceCard.css';

interface Track {
  id: 'timeline_healing' | 'womens_wellness' | 'spiritual' | 'manifestation' | 'nutrition';
  icon: React.ReactNode;
  title: string;
  shortDescription: string;
  researchHighlight: string;
  detailedResearch: string;
  benefits: string[];
}

const tracks: Track[] = [
  {
    id: 'timeline_healing',
    icon: <Sparkles className="h-8 w-8 text-[#aad8d6]" />,
    title: 'Timeline Reflection (Past - Present - Future)',
    shortDescription: 'A unified reflection journey that helps you understand your past, notice your present, and consciously shape your future self.',
    researchHighlight: 'A whole-timeline approach to self-reflection.',
    detailedResearch: `This track supports you to reflect across time - past patterns, present state and future self.

Many people find that combining reflection on earlier experiences with future-focused intention helps them feel a stronger sense of continuity, meaning and self-direction.

This is a self-reflection framework, not a clinical intervention. It is not a substitute for professional therapy or medical care.`,
    benefits: [
      'Notice how past patterns may shape present behaviours',
      'Track how you feel across mental, physical and spiritual dimensions',
      'Reflect on the future self you want to become',
      'See how past, present and future interconnect in your own story',
    ],
  },
  {
    id: 'womens_wellness',
    icon: <Heart className="h-8 w-8 text-[#aad8d6]" />,
    title: "Women's Wellness Track",
    shortDescription: 'Cycle awareness and reflection prompts to help you understand your body better.',
    researchHighlight: 'Cycle awareness as a tool for self-understanding.',
    detailedResearch: `Cycle tracking is a widely used self-awareness practice that can help you notice patterns in mood, energy and physical wellbeing across the month.

This track supports body literacy and reflection. It is not a diagnostic or medical tool and should not be used to make health decisions - please speak with your GP or a qualified clinician about any medical concerns.`,
    benefits: [
      'Greater body awareness and health literacy',
      'A framework for noticing patterns in mood and energy',
      'Space to reflect on emotional shifts across your cycle',
      'Support for planning around your natural rhythms',
    ],
  },
  {
    id: 'spiritual',
    icon: <Sparkles className="h-8 w-8 text-[#aad8d6]" />,
    title: 'Spiritual Practices & Intuition',
    shortDescription: 'A reflective space to explore intuition, meaning-making and spiritual practice.',
    researchHighlight: 'A complementary reflective practice, not a clinical tool.',
    detailedResearch: `This track offers reflective and spiritual practices - including meditation, journaling and intuitive exploration - that many people find supportive during grief, transition and meaning-making.

These are complementary wellbeing practices. They are not psychological treatment and are not offered as clinical care.`,
    benefits: [
      'Space to explore meaning and continuing bonds after loss',
      'Prompts for intuitive awareness and reflection',
      'Reflective tools to support how you cope with grief',
      'A sense of spiritual connection on your own terms',
    ],
  },
  {
    id: 'manifestation',
    icon: <Brain className="h-8 w-8 text-[#aad8d6]" />,
    title: 'Future Self & Intention Setting',
    shortDescription: 'Visualise your future, set intentions, and use guided reflection to clarify direction.',
    researchHighlight: 'Structured reflection on the future self you want to become.',
    detailedResearch: `Visualisation and intention-setting are established self-reflection practices that many people use to clarify goals and life direction.

This track offers prompts and guided reflections. It is not a promise of outcomes and is not a clinical intervention.`,
    benefits: [
      'A clearer sense of your future direction',
      'Structured prompts for intention and goal setting',
      'A reflective space to imagine and rehearse change',
      'A sense of agency in shaping what comes next',
    ],
  },
  {
    id: 'nutrition',
    icon: <Apple className="h-8 w-8 text-[#aad8d6]" />,
    title: 'Food-Mood Tracker',
    shortDescription: 'Notice how what you eat interacts with how you feel.',
    researchHighlight: 'A reflective food-and-mood log for self-awareness.',
    detailedResearch: `The food-mood tracker helps you notice patterns between what you eat and how you feel across the day.

This is a self-awareness tool. It does not provide medical, nutritional or dietary advice - please consult a qualified professional for any health decisions.`,
    benefits: [
      'Notice patterns between food and mood',
      'A reflective space to observe energy and clarity',
      'Prompts for mindful eating',
      'Greater awareness of your own food-mood connection',
    ],
  },
];

interface WellnessOnboardingProps {
  onComplete: () => void;
  skipSaveOnComplete?: boolean;
}

export default function WellnessOnboarding({ onComplete, skipSaveOnComplete = false }: WellnessOnboardingProps) {
  const [selectedTracks, setSelectedTracks] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTrack, setSelectedTrack] = useState<Track | null>(null);

  const toggleTrack = (trackId: string) => {
    const newSelection = new Set(selectedTracks);
    if (newSelection.has(trackId)) {
      newSelection.delete(trackId);
    } else {
      newSelection.add(trackId);
    }
    setSelectedTracks(newSelection);
  };

  const handleComplete = async () => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const updateData: Record<string, unknown> = {
        track_timeline_healing: selectedTracks.has('timeline_healing'),
        track_womens_wellness: selectedTracks.has('womens_wellness'),
        track_spiritual: selectedTracks.has('spiritual'),
        track_manifestation: selectedTracks.has('manifestation'),
        track_nutrition: selectedTracks.has('nutrition'),
        updated_at: new Date().toISOString(),
      };

      // Only mark onboarding complete if not skipping save
      if (!skipSaveOnComplete) {
        updateData.onboarding_completed = true;
        updateData.onboarding_completed_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from('profiles')
        .update(updateData as any)
        .eq('user_id', user.id);

      if (error) throw error;

      toast.success('Preferences saved successfully');
      onComplete();
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Failed to save preferences. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = async () => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Only mark onboarding complete if not skipping save
      if (!skipSaveOnComplete) {
        const { error } = await supabase
          .from('profiles')
          .update({
            onboarding_completed: true,
            onboarding_completed_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })
          .eq('user_id', user.id);

        if (error) throw error;
      }

      toast.success('You can enable wellness tracks anytime in Settings');
      onComplete();
    } catch (error) {
      console.error('Error skipping onboarding:', error);
      toast.error('Failed to skip. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-[#2F5354] via-[#2F5354] to-[#4ba2a4]/20 py-12 px-6">
        <div className="max-w-4xl mx-auto space-y-8">
          <div className="text-center space-y-4">
            <h1 className="text-4xl md:text-5xl font-light text-[hsl(30,30%,95%)] tracking-wide animate-fade-in">
              Personalise Your Wellness Journey
            </h1>
            <p className="text-lg text-[hsl(177,41%,76%)] max-w-3xl mx-auto font-light leading-relaxed">
              A reflective AI companion that works across your whole timeline - supporting you to reflect on your past, notice your present and shape your future - using biometric insight, psychological reflection and wellbeing tools across every level of your being: physical, emotional, mental, spiritual and relational. Therappi® is a self-reflection tool, not a substitute for clinical care.
            </p>
            <p className="text-base text-[hsl(177,41%,76%)] max-w-2xl mx-auto font-light">
              Select the areas that resonate with you right now. You can update these anytime in Settings - Preferences.
            </p>
            <p className="text-base text-[hsl(42,34%,73%)] max-w-2xl mx-auto font-light italic">
              Reflective practices inspired by evidence-informed psychology, alongside complementary self-care approaches.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:auto-rows-fr">
            {tracks.map((track) => (
              <div key={track.id} className="border-trace-wrapper flex">
                <svg className="border-trace-svg" preserveAspectRatio="none">
                  <rect
                    className="border-trace-path"
                    x="1"
                    y="1"
                    width="calc(100% - 2px)"
                    height="calc(100% - 2px)"
                    rx="8"
                    pathLength="1000"
                  />
                </svg>
                <Card
                  className="border-trace-content backdrop-blur-sm border-2 border-[hsl(177,41%,76%)]/40 hover:border-[hsl(182,37%,46%)] hover:shadow-xl transition-all duration-700 cursor-pointer hover:scale-[1.02] group overflow-hidden flex-1"
                  onClick={() => toggleTrack(track.id)}
                >
                <div className="absolute inset-0 bg-gradient-to-br from-[#4ba2a4]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                <CardHeader className="relative">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-full bg-gradient-to-br from-[#4ba2a4]/30 to-[#aad8d6]/30">
                        {track.icon}
                      </div>
                      <div className="flex-1">
                        <CardTitle className="text-xl font-light text-[hsl(30,30%,95%)] tracking-wide">
                          {track.title}
                        </CardTitle>
                      </div>
                    </div>
                    <Checkbox
                      checked={selectedTracks.has(track.id)}
                      onCheckedChange={() => toggleTrack(track.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="border-[hsl(177,41%,76%)] data-[state=checked]:bg-[hsl(182,37%,46%)] data-[state=checked]:border-[hsl(182,37%,46%)]"
                    />
                  </div>
                  <CardDescription className="text-[hsl(177,41%,76%)] font-light">
                    {track.shortDescription}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3 relative">
                  <div className="bg-[hsl(177,41%,76%)]/10 border border-[hsl(177,41%,76%)]/20 p-3 rounded-lg">
                    <p className="text-sm text-[hsl(30,30%,95%)] font-light">
                      {track.researchHighlight}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedTrack(track);
                    }}
                    className="w-full text-[hsl(42,34%,73%)] hover:text-[hsl(30,30%,95%)] hover:bg-[hsl(177,41%,76%)]/20 border border-[hsl(177,41%,76%)]/30 hover:border-[hsl(177,41%,76%)]/60"
                  >
                    <Info className="h-4 w-4 mr-2" />
                    View Research Details
                  </Button>
                </CardContent>
              </Card>
              </div>
            ))}
          </div>

          <Card className="bg-[#2F5354]/40 backdrop-blur-sm border-2 border-[hsl(177,41%,76%)]/40 hover:border-[hsl(182,37%,46%)] hover:shadow-xl transition-all duration-700 animate-border-pulse">
            <CardHeader>
              <CardTitle className="text-xl font-light text-[hsl(30,30%,95%)] tracking-wide">
                Core Features (Always Available)
              </CardTitle>
              <CardDescription className="text-[hsl(177,41%,76%)] font-light">
                These essential tools are available to all users:
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  'AI Therappi®',
                  'The Sanctuary',
                  'Crisis & Safety Suite',
                  'Daily Check-In',
                  'Biometric Integration (Therappi & Whoop)',
                  'Sleep Suite',
                  'The Vault',
                  'Connect & Grow',
                  'Nervous System Reset',
                  'Quantum Journalling',
                ].map((feature) => (
                  <div
                    key={feature}
                    className="flex items-center gap-2 text-[hsl(30,30%,95%)] font-light"
                  >
                    <div className="h-2 w-2 rounded-full bg-gradient-to-br from-[#4ba2a4] to-[#aad8d6] animate-pulse-subtle" />
                    {feature}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-col items-center gap-4">
            <div className="flex justify-center gap-4 flex-wrap">
              <Button
                onClick={handleSkip}
                disabled={isLoading}
                size="lg"
                variant="outline"
                className="border-2 border-[hsl(177,41%,76%)]/60 text-[hsl(30,30%,95%)] hover:bg-[hsl(177,41%,76%)]/20 hover:border-[hsl(177,41%,76%)] px-8 font-light transition-all duration-300"
              >
                Skip for Now
              </Button>
              <Button
                onClick={handleComplete}
                disabled={isLoading}
                size="lg"
                className="bg-gradient-to-br from-[#4ba2a4] to-[#aad8d6] text-white hover:shadow-xl hover:scale-105 px-12 font-light transition-all duration-300"
              >
                {isLoading ? 'Saving...' : 'Continue to Therappi'}
              </Button>
            </div>

            <p className="text-center text-sm text-[hsl(177,41%,76%)] font-light">
              You can modify these preferences at any time in Settings → Preferences.
            </p>
          </div>
        </div>
      </div>

      <Dialog open={!!selectedTrack} onOpenChange={() => setSelectedTrack(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-gradient-to-br from-[#2F5354] to-[#2F5354]/90 border-2 border-[hsl(177,41%,76%)]/40">
          <DialogHeader>
            <DialogTitle className="text-2xl font-light text-[hsl(30,30%,95%)] flex items-center gap-3 tracking-wide">
              <div className="p-2 rounded-full bg-gradient-to-br from-[#4ba2a4]/30 to-[#aad8d6]/30">
                {selectedTrack?.icon}
              </div>
              {selectedTrack?.title}
            </DialogTitle>
            <DialogDescription className="text-[hsl(177,41%,76%)] font-light">
              Evidence-based research supporting this wellness track
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-[hsl(177,41%,76%)]/10 border border-[hsl(177,41%,76%)]/20 p-4 rounded-lg">
              <p className="text-sm text-[hsl(30,30%,95%)] font-light whitespace-pre-line leading-relaxed">
                {selectedTrack?.detailedResearch}
              </p>
            </div>
            <div>
              <h4 className="font-light text-[hsl(42,34%,73%)] mb-2 tracking-wide">Key Benefits:</h4>
              <ul className="space-y-2">
                {selectedTrack?.benefits.map((benefit, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-[hsl(30,30%,95%)] font-light">
                    <div className="h-2 w-2 rounded-full bg-gradient-to-br from-[#4ba2a4] to-[#aad8d6] mt-2 flex-shrink-0" />
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
            <p className="text-xs text-[hsl(177,41%,76%)]/80 font-light italic">
              * All statistics cited from peer-reviewed research journals.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
